/**
 * 聊天状态管理
 */

import { create } from 'zustand';
import type { Message, ConfirmationRequest, InteractivePrompt, BackgroundTaskInfo, OperationInfo } from '@/types';

export type ChatInteractionMode = 'agent' | 'ask';

interface ChatState {
  // 消息列表
  messages: Message[];
  
  // 输入状态
  inputValue: string;
  isProcessing: boolean;
  
  // 待运行消息
  pendingMessage: string | null;
  
  // 当前思考内容（流式 JSON 解析出的 thought，对齐旧版 thinking-box）
  currentThinking: string | null;
  /** 服务端 status 单行展示，避免每条都塞进消息列表造成刷屏 */
  liveStatus: string | null;

  /** 实时命令输出（SSH 执行命令时的流式 stdout/stderr） */
  liveCommandOutput: string | null;
  /** 当前正在执行的命令 */
  currentCommand: string | null;
  /** 命令输出是否已完成（完成后保留显示直到下次处理） */
  commandOutputDone: boolean;

  /** 正在流式生成的最终答案（逐字累积，完成后转为正式消息） */
  streamingAnswer: string | null;

  /** 后台任务列表（运行中的任务，用于实时面板显示） */
  backgroundTasks: BackgroundTaskInfo[];
  /** 当前运维 operation（长任务生命周期） */
  currentOperation: OperationInfo | null;
  /** 服务端会话历史估算上下文字节数；比本地消息列表更稳定 */
  serverContextBytes: number | null;
  
  // 确认对话框
  confirmation: ConfirmationRequest | null;
  
  // 交互式输入
  interactivePrompt: InteractivePrompt | null;
  
  // 会话信息
  sessionId: string;

  /** Agent：可执行工具；Ask：仅对话解答，不执行命令 */
  chatMode: ChatInteractionMode;
  setChatMode: (mode: ChatInteractionMode) => void;

  /** 中止回调，由 useMessages 注册 */
  abortChatFn: (() => void) | null;
  
  // Actions
  addMessage: (message: Omit<Message, 'id' | 'timestamp'>) => void;
  updateMessage: (id: string, updates: Partial<Message>) => void;
  clearMessages: () => void;
  /** 整表替换（用于从 IndexedDB 恢复，避免关闭再打开时重复追加） */
  replaceMessages: (messages: Message[]) => void;

  /**
   * 从某条用户消息起截断后续记录并替换为该条新文案（用于「编辑后重新发送」）。
   * 不自动发 WebSocket，由调用方再 emit chat。
   */
  branchFromUserMessage: (messageId: string, newContent: string) => boolean;
  
  setInputValue: (value: string) => void;
  setIsProcessing: (value: boolean) => void;
  
  setPendingMessage: (message: string | null) => void;
  setCurrentThinking: (thinking: string | null) => void;
  setLiveStatus: (text: string | null) => void;
  setLiveCommandOutput: (output: string | null) => void;
  appendLiveCommandOutput: (chunk: string) => void;
  setCurrentCommand: (command: string | null) => void;
  setCommandOutputDone: (done: boolean) => void;
  /** 追加流式最终答案 chunk */
  appendAnswerChunk: (delta: string) => void;
  /** 直接设置流式答案完整内容（用于 final_answer_chunk 的 full 字段） */
  setStreamingAnswer: (text: string | null) => void;
  /** 流式答案完成，转为正式消息并清空流式状态 */
  finalizeAnswer: (fullText?: string) => void;
  /** 添加或更新后台任务（按 taskId 去重） */
  upsertBackgroundTask: (task: BackgroundTaskInfo) => void;
  /** 移除指定后台任务 */
  removeBackgroundTask: (taskId: string) => void;
  /** 更新后台任务状态（如轮询进度） */
  updateBackgroundTask: (taskId: string, updates: Partial<BackgroundTaskInfo>) => void;
  /** D4: 累加后台任务实时输出（E3: seq 用于断连重连去重） */
  appendBackgroundTaskOutput: (taskId: string, data: string, type?: string, seq?: number) => void;
  /** E2: 更新后台任务最后接收的事件序列号 */
  setBackgroundTaskLastSeq: (taskId: string, seq: number) => void;
  setCurrentOperation: (operation: OperationInfo | null) => void;
  setServerContextBytes: (bytes: number | null) => void;
  setAbortChatFn: (fn: (() => void) | null) => void;
  clearAgentLiveUi: () => void;
  
  showConfirmation: (request: ConfirmationRequest) => void;
  hideConfirmation: () => void;
  
  showInteractivePrompt: (prompt: InteractivePrompt) => void;
  hideInteractivePrompt: () => void;
  
  generateSessionId: () => string;
}

// 生成唯一 ID
const generateId = () => `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const useChatStore = create<ChatState>((set, get) => ({
  // 初始状态
  messages: [],
  inputValue: '',
  isProcessing: false,
  pendingMessage: null,
  currentThinking: null,
  liveStatus: null,
  liveCommandOutput: null,
  currentCommand: null,
  commandOutputDone: false,
  streamingAnswer: null,
  backgroundTasks: [],
  currentOperation: null,
  serverContextBytes: null,
  confirmation: null,
  interactivePrompt: null,
  sessionId: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  chatMode: 'agent' as ChatInteractionMode,
  abortChatFn: null,

  // 添加消息
  addMessage: (message) => {
    const newMessage: Message = {
      ...message,
      id: generateId(),
      timestamp: Date.now(),
    };
    
    set((state) => ({
      messages: [...state.messages, newMessage],
    }));
    
    return newMessage;
  },
  
  // 更新消息
  updateMessage: (id, updates) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id ? { ...msg, ...updates } : msg
      ),
    }));
  },
  
  // 清空消息
  clearMessages: () => {
    set({
      messages: [],
      currentThinking: null,
      liveStatus: null,
      liveCommandOutput: null,
      streamingAnswer: null,
      currentOperation: null,
      serverContextBytes: null,
      sessionId: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    });
  },

  replaceMessages: (messages) =>
    set({
      messages,
      currentThinking: null,
      liveStatus: null,
      liveCommandOutput: null,
      streamingAnswer: null,
      currentOperation: null,
      serverContextBytes: null,
    }),

  branchFromUserMessage: (messageId, newContent) => {
    const trimmed = newContent.trim();
    if (!trimmed) return false;
    const state = get();
    const sid = String(messageId);
    const idx = state.messages.findIndex((m) => String(m.id) === sid);
    if (idx < 0 || state.messages[idx].role !== 'user') return false;
    const kept = state.messages.slice(0, idx);
    const newUser: Message = {
      id: generateId(),
      role: 'user',
      content: trimmed,
      timestamp: Date.now(),
    };
    set({
      messages: [...kept, newUser],
      currentThinking: null,
      liveStatus: null,
      liveCommandOutput: null,
      currentOperation: null,
      serverContextBytes: null,
    });
    return true;
  },
  
  // 输入值
  setInputValue: (value) => set({ inputValue: value }),
  
  // 处理状态
  setIsProcessing: (value) => set({ isProcessing: value }),
  
  // 待运行消息
  setPendingMessage: (message) => set({ pendingMessage: message }),
  
  // 当前思考
  setCurrentThinking: (thinking) => set({ currentThinking: thinking }),

  setLiveStatus: (text) => set({ liveStatus: text }),

  setLiveCommandOutput: (output) => set({ liveCommandOutput: output }),

  appendLiveCommandOutput: (chunk) =>
    set((state) => ({
      liveCommandOutput: (state.liveCommandOutput || '') + chunk,
    })),

  setCurrentCommand: (command) => set({ currentCommand: command }),

  setCommandOutputDone: (done) => set({ commandOutputDone: done }),

  appendAnswerChunk: (delta) =>
    set((state) => ({
      streamingAnswer: (state.streamingAnswer || '') + delta,
    })),

  setStreamingAnswer: (text) => set({ streamingAnswer: text }),

  finalizeAnswer: (fullText) => {
    const st = get();
    const answer = fullText != null ? fullText : (st.streamingAnswer || '');
    const trimmed = answer.trim();
    if (trimmed) {
      const newMessage: Message = {
        id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        role: 'assistant',
        content: trimmed,
        assistantKind: 'final_answer',
        timestamp: Date.now(),
      };
      set((state) => ({
        messages: [...state.messages, newMessage],
        streamingAnswer: null,
      }));
    } else {
      set({ streamingAnswer: null });
    }
  },

  upsertBackgroundTask: (task) =>
    set((state) => {
      const idx = state.backgroundTasks.findIndex((t) => t.taskId === task.taskId);
      if (idx >= 0) {
        const updated = [...state.backgroundTasks];
        updated[idx] = { ...updated[idx], ...task };
        return { backgroundTasks: updated };
      }
      return { backgroundTasks: [...state.backgroundTasks, task] };
    }),

  removeBackgroundTask: (taskId) =>
    set((state) => ({
      backgroundTasks: state.backgroundTasks.filter((t) => t.taskId !== taskId),
    })),

  updateBackgroundTask: (taskId, updates) =>
    set((state) => ({
      backgroundTasks: state.backgroundTasks.map((t) =>
        t.taskId === taskId ? { ...t, ...updates } : t
      ),
    })),

  /** D4: 累加后台任务实时输出（task_progress 事件）。E3: seq 去重，防止断连重连重放重复累加 */
  appendBackgroundTaskOutput: (taskId, data, _type, seq) =>
    set((state) => {
      const idx = state.backgroundTasks.findIndex((t) => t.taskId === taskId);
      if (idx < 0) return {};
      const task = state.backgroundTasks[idx];
      // E3: 若带 seq 且 <= lastSeq，说明是已处理过的重放事件，丢弃避免重复累加
      if (typeof seq === 'number' && typeof task.lastSeq === 'number' && seq <= task.lastSeq) {
        return {};
      }
      const newOutput = (task.output || '') + data;
      // 截断：保留最后 50000 字符，防止内存爆炸
      const trimmed = newOutput.length > 50000 ? newOutput.slice(-50000) : newOutput;
      const updated = [...state.backgroundTasks];
      updated[idx] = {
        ...task,
        output: trimmed,
        outputTail: trimmed.slice(-5000),
        lastSeq: typeof seq === 'number' ? seq : task.lastSeq,
      };
      return { backgroundTasks: updated };
    }),

  /** E2: 更新后台任务最后接收的事件序列号 */
  setBackgroundTaskLastSeq: (taskId, seq) =>
    set((state) => ({
      backgroundTasks: state.backgroundTasks.map((t) =>
        t.taskId === taskId ? { ...t, lastSeq: seq } : t
      ),
    })),

  setCurrentOperation: (operation) => set({ currentOperation: operation }),

  setServerContextBytes: (bytes) => set({ serverContextBytes: bytes }),

  clearAgentLiveUi: () =>
    set({
      currentThinking: null,
      liveStatus: null,
      liveCommandOutput: null,
      currentCommand: null,
      commandOutputDone: false,
      streamingAnswer: null,
    }),
  
  // 确认对话框
  showConfirmation: (request) => set({ confirmation: request }),
  hideConfirmation: () => set({ confirmation: null }),
  
  // 交互式输入
  showInteractivePrompt: (prompt) => set({ interactivePrompt: prompt }),
  hideInteractivePrompt: () => set({ interactivePrompt: null }),
  
  setChatMode: (mode) => set({ chatMode: mode }),

  setAbortChatFn: (fn) => set({ abortChatFn: fn }),

  // 生成会话 ID
  generateSessionId: () => {
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    set({ sessionId: newSessionId });
    return newSessionId;
  },
}));